import type { ReactNode } from 'react';
import { Icon } from '@/components/primitives';
import { useViewport } from '@/lib/viewport';

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  breadcrumb?: string[];
  actions?: ReactNode;
  status?: ReactNode;
  children?: ReactNode;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumb,
  actions,
  status,
  children,
}: PageHeaderProps) {
  const { isMobile } = useViewport();
  const stacked = isMobile;
  return (
    <div
      style={{
        padding: stacked ? '14px 16px 12px' : '20px 24px 16px',
        borderBottom: '1px solid var(--bx-border-muted)',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {breadcrumb && !stacked && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11.5,
            color: 'var(--bx-text-muted)',
            flexWrap: 'wrap',
          }}
        >
          {breadcrumb.map((b, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: i === breadcrumb.length - 1 ? 600 : 400,
                color:
                  i === breadcrumb.length - 1
                    ? 'var(--bx-text-dark)'
                    : 'var(--bx-text-muted)',
                whiteSpace: 'nowrap',
              }}
            >
              {i > 0 && <Icon name="chevron-right" size={9} />}
              {b}
            </span>
          ))}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: stacked ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: stacked ? 10 : 16,
          flexDirection: stacked ? 'column' : 'row',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minWidth: 0,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                fontSize: stacked ? 18 : 22,
                fontWeight: 600,
                color: 'var(--bx-text-heading)',
                margin: 0,
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <div
                style={{
                  fontSize: stacked ? 12 : 13,
                  color: 'var(--bx-text-muted)',
                  marginTop: 4,
                  lineHeight: 1.45,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {status && <div style={{ marginLeft: 4 }}>{status}</div>}
        </div>
        {actions && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {actions}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
